import React, { PureComponent } from 'react'
import { withTheme } from 'styled-components'
import BootstrapTable from 'react-bootstrap-table-next'
import { Paragraph } from 'components/atoms/Texts/TxtParagraph'
import { v4 as uuidv4 } from 'uuid';

const rowStyle = (row, index) => ({
  backgroundColor: row.isTotalRow ? '#dee2e6' : index % 2 === 0 ? '#F1EFFA' : 'white',
  overflowWrap: 'break-word',
});

const errorTemplate = (cell, align="") => {
  const value = typeof cell.value === 'number' ? String(cell.value).toLocaleString('en-US') : cell.value;

  return (
    <>
      <Paragraph mb={'0px'} weight={'bold'} size={'14px'} textAlign={align}>{value}</Paragraph>
      <Paragraph mb={'0px'} color='red' weight={'bold'} size={'14px'}>{cell.error}</Paragraph>
    </>
  )
}

const commonHeaderStyle = { textAlign: 'left', verticalAlign: 'middle' }

const columns = [
  { 
    dataField: 'area', 
    text: 'Area',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: function callback(cell, row, rowIndex, colIndex) {
      if (row.isTotalRow) {
        return { colSpan: 6 };
      }
      return row.rowSpan ? { title: 'Area', rowSpan: row.rowSpan } : { title: 'Area', hidden: true };
    },
    formatter: (cell, row) => {
      if (row.isTotalRow) {
        return (
          <Paragraph mb={'0px'} weight={'bold'} size={'14px'}>Total Tonnage</Paragraph>
        );
      }
      return cell.error ? (
        errorTemplate(cell, 'left')
      ) : (
        <Paragraph mb={'0px'} weight={'bold'} size={'14px'}>{cell.value}</Paragraph>
      );
    },
  },
  {
    dataField: 'coalLocation',
    text: 'Coal Location',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: function callback(cell, row, rowIndex, colIndex) {
      if (row.type === 'summary') {
        return { colSpan: 5 };
      }

      return (row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}
    },
    formatter: (cell, row) => {
      return cell.error ? (
        errorTemplate(cell, 'left')
      ) : (
        <Paragraph mb={'0px'} weight={(row.type === 'summary' ? 'bold' : 'normal')} size={'14px'}>{cell.value}</Paragraph>
      )
    },
  },
  {
    dataField: 'coalpile',
    text: 'Pile No',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: (cell, row) => ((row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}),
    formatter: (cell) =>
      cell.error ? (
        errorTemplate(cell, 'left')
      ) : (
        <Paragraph mb={'0px'} weight={'normal'} size={'14px'}>{cell.value}</Paragraph>
      ),
  },
  { 
    dataField: 'coalProduct', 
    text: 'Coal Product',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: (cell, row) => ((row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}),
    formatter: (cell) =>
      cell.error ? (
        errorTemplate(cell, 'left')
      ) : (
        <Paragraph mb={'0px'} weight={'normal'} size={'14px'}>{cell.value}</Paragraph>
      ),
  },
  { 
    dataField: 'volume', 
    text: 'Volume',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: (cell, row) => ((row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}),
    formatter: (cell) =>
      cell.error ? (
        errorTemplate(cell, 'right')
      ) : (
        
      <Paragraph mb={'0px'} weight={'normal'} size={'14px'} textAlign={'right'}>
        {cell.value ? cell.value.toLocaleString('en-US') : cell.value}
      </Paragraph>
      ),
  },
  {
    dataField: 'factorScale',
    text: 'Factor Scale',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: (cell, row) => ((row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}),
    formatter: (cell) => cell.error ? (
      errorTemplate(cell, 'right')
    ) : (
      <Paragraph mb={'0px'} weight={'normal'} size={'14px'} textAlign={'right'}>
        {cell.value ? cell.value.toLocaleString('en-US') : cell.value}
      </Paragraph>
    )
  },
  { 
    dataField: 'tonnage', 
    text: 'Tonnage',
    style: { verticalAlign: 'middle' },
    headerStyle: { ...commonHeaderStyle, width: '200px' },
    attrs: function callback(cell, row, rowIndex, colIndex) {
      if (row.isTotalRow || row.type === 'summary') {
        return { colSpan: 1 };
      }
    },
    formatter: (cell, row) => {
      if (row.isTotalRow) {
        return (
          <Paragraph mb={'0px'} ml={'50px'} weight={'bold'} size={'14px'} textAlign={'right'}>{row.tonnage.toLocaleString('en-US')}</Paragraph>
        );
      }
      if (row.type === 'summary') {
        return (
          <Paragraph mb={'0px'} ml={'50px'} weight={'bold'} size={'14px'} textAlign={'right'}>{row.totalTonnage.toLocaleString('en-US')}</Paragraph>
        );
      }
      return (
      <Paragraph mb={'0px'} weight={'normal'} size={'14px'} textAlign={'right'}>
        {cell ? cell.toLocaleString('en-US') : cell}
      </Paragraph>
    )
    },
  },
  { 
    dataField: 'startTime', 
    text: 'Start Time',
    style: { verticalAlign: 'middle' },
    headerStyle: { ...commonHeaderStyle, width: '70px' },
    attrs: function callback(cell, row, rowIndex, colIndex) {
      if (row.isTotalRow || row.type === 'summary') {
        return { colSpan: 3 };
      }
    },
    formatter: (cell) => {
      if (!cell.value) return;

      const [hours, minutes] = cell.value.split(":");
      return cell.error ? (
        errorTemplate(cell, 'left')
      ) : (
        <Paragraph mb={'0px'} weight={'normal'} size={'14px'}>{`${hours}:${minutes}`}</Paragraph>
      )
    }
  },
  { 
    dataField: 'endTime', 
    text: 'End Time',
    style: { verticalAlign: 'middle' },
    headerStyle: { ...commonHeaderStyle, width: '70px' },
    attrs: (cell, row) => ((row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}),
    formatter: (cell) => {
      if (!cell.value) return;

      const [hours, minutes] = cell.value.split(":");
      return cell.error ? (
        errorTemplate(cell, 'left')
      ) : (
        <Paragraph mb={'0px'} weight={'normal'} size={'14px'}>{`${hours}:${minutes}`}</Paragraph>
      )
    }
  },
  { 
    dataField: 'remark', 
    text: 'Remark',
    style: { verticalAlign: 'middle' },
    headerStyle: commonHeaderStyle,
    attrs: (cell, row) => ((row.isTotalRow || row.type === 'summary') ? { hidden: true } : {}),
    formatter: (cell) => (
      <Paragraph mb={'0px'} weight={'normal'} size={'14px'}>{cell}</Paragraph>
    ),
  },
]

const formatDataForTable = (data) => {
  return data.flatMap((inventory) => {
    const area = inventory.areaCode;
    const rowSpan = inventory.rowSpan;
    return inventory.coalStockInventoryDetails.map((detail) => ({
      id: uuidv4(),
      area,
      rowSpan,
      coalLocation: (typeof detail.dirLocationCode === 'string') ? { value: detail.dirLocationCode, error: null } : detail.dirLocationCode,
      coalpile: detail.coalpile || { value: null, error: null },
      coalProduct: detail.productName || { value: null, error: null },
      volume: detail.volume || { value: null, error: null },
      factorScale: detail.factorScale || { value: null, error: null },
      tonnage: detail.tonnage || 0,
      startTime: detail.startTime || { value: null, error: null },
      endTime: detail.endTime || { value: null, error: null },
      remark: detail.remark,
      type: detail.type || '',
      totalTonnage: detail.totalTonnage || 0
    }));
  });
};

const calculateRowSpanAndTotals = (data) => {
  let areaSeen = {};
  const result = [];

  const areaCounts = data.reduce((acc, item) => {
    const area = item.area.value;
    acc[area] = (acc[area] || 0) + 1;
    return acc;
  }, {});

  const areaTotals = data.reduce((acc, item) => {
    const area = item.area.value;
    acc[area] = (acc[area] || 0) + item.tonnage;
    return acc;
  }, {});

  data.forEach((item, index) => {
    const area = item.area.value;
    if (!areaSeen[area]) {
      areaSeen[area] = { count: 0, lastIndex: 0 };
    }

    areaSeen[area].count++;
    result.push(areaSeen[area].count === 1 ? { ...item, rowSpan: areaCounts[area] } : { ...item });

    if (areaSeen[area].count === areaCounts[area]) {
      result.push({
        id: uuidv4(),
        area: { value: area, error: null },
        coalLocation: { value: null, error: null },
        coalpile: { value: null, error: null },
        coalProduct: { value: null, error: null },
        volume: { value: null, error: null },
        factorScale: { value: null, error: null },
        startTime: { value: null, error: null },
        endTime: { value: null, error: null },
        remark: null,
        tonnage: areaTotals[area],
        isTotalRow: true,
        rowSpan: 1,
        type: null,
        totalTonnage: null
      });
    }
  });

  return result;
};

export class ImportExcelTablePreview extends PureComponent {
  render() {
    const { coalStockInventories } = this.props

    const data = formatDataForTable(coalStockInventories);
    const result = calculateRowSpanAndTotals(data)

    return (
      <BootstrapTable
        keyField="id"
        noDataIndication="Table is Empty"
        bordered={true}
        hover
        defaultSorted={undefined}
        rowStyle={rowStyle}
        columns={columns}
        data={result}
      />
    )
  }
}

export default withTheme(ImportExcelTablePreview)
